import { Injectable } from '@angular/core';
import { arrayRemove } from '@datorama/akita';
import {
  HttpMethod,
  NgEntityService,
  NgEntityServiceConfig,
} from '@datorama/akita-ng-entity-service';
import { of } from 'rxjs';
import { map, tap } from 'rxjs/operators';
import { AuthQuery } from '../auth';
import { Notification } from '../notification';
import { Group } from './group.model';
import { GroupQuery } from './group.query';
import { GroupState, GroupStore } from './group.store';

@NgEntityServiceConfig({
  resourceName: 'groups',
})
@Injectable()
export class GroupService extends NgEntityService<GroupState> {
  constructor(
    protected store: GroupStore,
    private query: GroupQuery,
    private authQuery: AuthQuery
  ) {
    super(store);
  }

  setActive(id?: string) {
    if (!id) {
      this.store.removeActive(this.query.getActiveId());
      return;
    }

    this.store.setActive(id);
    if (!this.query.getHasCache()) {
      this.get().subscribe();
    }
  }

  removeUser(groupId: string, userId: string) {
    return this.getHttp()
      .delete(`${this.api}/${groupId}/users/${userId}`)
      .pipe(
        tap(() =>
          this.store.update(groupId, (group) => ({
            ...group,
            users: arrayRemove(group.users, userId),
          }))
        )
      );
  }

  createLink(id: string) {
    return this.getHttp()
      .post<Group>(`${this.api}/${id}/token`, {})
      .pipe(
        map(({ token }) => {
          this.store.update(id, (state) => ({ ...state, token }));
          const { entities } = this.store.getValue();
          return entities && entities[id];
        })
      );
  }

  joinGroup(id: string, token: string) {
    return this.getHttp().post(`${this.api}/${id}/request`, { token });
  }

  isOwner(group: Group) {
    return group?.createdBy.id === this.authQuery.getValue()?.user?.id;
  }

  updateProperty(id: string, property: string, value: any) {
    this.update(
      id,
      { [property]: value },
      { method: HttpMethod.PATCH }
    ).subscribe();
  }

  byId(id: string, force = false) {
    const { entities } = this.store.getValue();
    const inStore = entities && entities[id];
    if (inStore && !force) {
      return of(inStore);
    }
    const params: any = {};

    if (force) {
      params.d = Date.now();
    }
    return super
      .getHttp()
      .get<Group>(`${this.api}/${id}`, { params })
      .pipe(
        tap((event) => {
          this.store.upsert(id, (state) => {
            return {
              ...event,
              extended: false,
            };
          });
        })
      );
  }

  updateGroup(id: string, groupToUpdate: Partial<Group>) {
    this.store.update(id, groupToUpdate);
  }

  playerJoinedGroupHandler(notification: Notification<any, Group>) {
    this.store.update(
      notification.relatedDocumentId,
      () => notification.relatedDocument
    );
  }

  removedFromGroupHandler({ relatedDocumentId: groupId }: Notification) {
    if (this.query.getActive()?.id === groupId) {
      this.store.removeActive(groupId);
    }
    const { user } = this.authQuery.getValue();
    this.store.update(groupId, ({ users }) => ({
      users: arrayRemove(users, [user.id])
    }));

  }
}
